import React, { useState, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import Text from 'core-system/Text'
import FlexContainer from 'core-system/FlexContainer'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import { AppState } from 'redux/config/store'
import { Segment } from 'redux/employer/employerTypes'
import { SegmentListType } from 'features/Programs/Shared/types'
import {
  AutoEnrollRule,
  AutoEnrollField,
  AutoEnrollOperator,
  ConditionJoin,
} from './autoEnrollTypes'
import EnrollmentTargetBlock from './components/EnrollmentTargetBlock'
import {
  RuleLogicEditor,
  firstFieldOtherThan,
  asStringArray,
  hasConditionValue,
  isListOperator,
  type RuleConditionValue,
} from 'features/RuleBuilder'

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const IntroTitle = styled(Text)`
  letter-spacing: 0.04em;
`

const WhenSurface = styled.div`
  margin-top: 1.25rem;
  border-radius: 0.75rem;
  border: 1px solid ${(p) => p.theme.palette.grey.grey300};
  background: ${(p) => p.theme.palette.grey.grey100};
  padding: 1.35rem 1.5rem;
`

const ThenSurface = styled.div`
  margin-top: 1rem;
  border-radius: 0.75rem;
  border: 1px solid ${(p) => p.theme.palette.grey.grey300};
  background: ${(p) => p.theme.palette.white};
  padding: 1.35rem 1.5rem;
`

const SectionEyebrow = styled(Text)`
  margin-bottom: 0.85rem;
`

const Actions = styled(FlexContainer)`
  margin-top: 1.25rem;
  justify-content: flex-end;
`

interface AddAutoEnrollRuleFormProps {
  onAddRule: (rule: Omit<AutoEnrollRule, 'id'>) => void
}

const normalizeConditionValue = (
  value: RuleConditionValue,
  operator: AutoEnrollOperator
) =>
  isListOperator(operator) ? asStringArray(value) : String(value).trim()

const AddAutoEnrollRuleForm: React.FC<AddAutoEnrollRuleFormProps> = ({
  onAddRule,
}) => {
  const segmentsMap = useSelector(
    (state: AppState) => state.employer.segmentsMap
  )
  const allTags = useSelector((state: AppState) => state.tags.allTags)

  const groupSegments = useMemo(() => {
    if (!segmentsMap) return []
    return Object.values(segmentsMap).filter(
      (s: Segment) => s.type === 'CUSTOM' && !s.archived
    ) as Segment[]
  }, [segmentsMap])

  const pickerItems: SegmentListType[] = useMemo(
    () =>
      groupSegments.map((s) => ({
        id: s.id,
        text: s.name,
        type: 'CUSTOM',
      })),
    [groupSegments]
  )

  const tagPickerItems: SegmentListType[] = useMemo(
    () =>
      (allTags ?? []).map((tag) => ({
        id: tag.id,
        text: tag.name,
        type: 'CUSTOM',
      })),
    [allTags]
  )

  const [field, setField] = useState<AutoEnrollField>('worksite_name')
  const [operator, setOperator] = useState<AutoEnrollOperator>('equals')
  const [value, setValue] = useState<RuleConditionValue>('')
  const [addSecondCondition, setAddSecondCondition] = useState(false)
  const [conditionJoin, setConditionJoin] = useState<ConditionJoin>('and')
  const [secondaryField, setSecondaryField] =
    useState<AutoEnrollField>('additional_info')
  const [secondaryOperator, setSecondaryOperator] =
    useState<AutoEnrollOperator>('equals')
  const [secondaryValue, setSecondaryValue] = useState<RuleConditionValue>('')
  const [addThirdCondition, setAddThirdCondition] = useState(false)
  const [tertiaryConditionJoin, setTertiaryConditionJoin] =
    useState<ConditionJoin>('and')
  const [tertiaryField, setTertiaryField] =
    useState<AutoEnrollField>('title')
  const [tertiaryOperator, setTertiaryOperator] =
    useState<AutoEnrollOperator>('equals')
  const [tertiaryValue, setTertiaryValue] = useState<RuleConditionValue>('')
  const [activeGroupItems, setActiveGroupItems] = useState<SegmentListType[]>(
    []
  )
  const [activeTagItems, setActiveTagItems] = useState<SegmentListType[]>([])
  const [isActive, setIsActive] = useState(true)
  const [removeNonMatchingMembers, setRemoveNonMatchingMembers] =
    useState(false)

  useEffect(() => {
    if (pickerItems.length === 0) {
      setActiveGroupItems([])
      return
    }
    setActiveGroupItems((prev) => {
      const kept = prev.filter((p) => pickerItems.some((d) => d.id === p.id))
      if (kept.length > 0) return kept
      return [pickerItems[0]]
    })
  }, [pickerItems])

  useEffect(() => {
    if (tagPickerItems.length === 0) {
      setActiveTagItems([])
      return
    }
    setActiveTagItems((prev) => {
      const kept = prev.filter((p) => tagPickerItems.some((d) => d.id === p.id))
      return kept
    })
  }, [tagPickerItems])

  useEffect(() => {
    if (!addSecondCondition) return
    if (secondaryField === field) {
      setSecondaryField(
        firstFieldOtherThan(
          field,
          ...(addThirdCondition ? [tertiaryField] : [])
        )
      )
    }
  }, [addSecondCondition, addThirdCondition, field, secondaryField, tertiaryField])

  useEffect(() => {
    if (!addThirdCondition) return
    if (tertiaryField === field || tertiaryField === secondaryField) {
      setTertiaryField(firstFieldOtherThan(field, secondaryField))
    }
  }, [addThirdCondition, field, secondaryField, tertiaryField])

  const hasSecondary =
    addSecondCondition &&
    hasConditionValue(secondaryValue, secondaryOperator)
  const hasTertiary =
    addThirdCondition &&
    hasConditionValue(tertiaryValue, tertiaryOperator)

  const handleAddSecondConditionChange = (next: boolean) => {
    if (!next) {
      setSecondaryValue('')
      setAddThirdCondition(false)
      setTertiaryValue('')
    }
    setAddSecondCondition(next)
  }

  const handleAddThirdConditionChange = (next: boolean) => {
    if (!next) {
      setTertiaryValue('')
    }
    setAddThirdCondition(next)
  }

  const handleAdd = () => {
    const segmentIds = activeGroupItems.map((i) => i.id).filter(Boolean)
    const tagIds = activeTagItems.map((i) => i.id).filter(Boolean)
    if (
      !hasConditionValue(value, operator) ||
      (segmentIds.length === 0 && tagIds.length === 0)
    ) {
      return
    }
    if (addSecondCondition && !hasSecondary) return
    if (addThirdCondition && !hasTertiary) return

    const base: Omit<AutoEnrollRule, 'id'> = {
      field,
      operator,
      value: normalizeConditionValue(value, operator),
      segments: segmentIds,
      tags: tagIds,
      isActive,
      removeNonMatchingMembers,
    }
    if (hasSecondary) {
      base.secondaryField = secondaryField
      base.secondaryOperator = secondaryOperator
      base.secondaryValue = normalizeConditionValue(
        secondaryValue,
        secondaryOperator
      )
      base.conditionJoin = conditionJoin
    }
    if (hasSecondary && hasTertiary) {
      base.tertiaryField = tertiaryField
      base.tertiaryOperator = tertiaryOperator
      base.tertiaryValue = normalizeConditionValue(
        tertiaryValue,
        tertiaryOperator
      )
      base.tertiaryConditionJoin = tertiaryConditionJoin
    }
    onAddRule(base)
    setValue('')
    setSecondaryValue('')
    setTertiaryValue('')
    setAddSecondCondition(false)
    setAddThirdCondition(false)
    setIsActive(true)
    setRemoveNonMatchingMembers(false)
    setConditionJoin('and')
    setTertiaryConditionJoin('and')
    setActiveGroupItems(pickerItems.length > 0 ? [pickerItems[0]] : [])
    setActiveTagItems([])
  }

  const canAdd =
    hasConditionValue(value, operator) &&
    (activeGroupItems.length > 0 || activeTagItems.length > 0) &&
    (!addSecondCondition ||
      hasConditionValue(secondaryValue, secondaryOperator)) &&
    (!addThirdCondition ||
      hasConditionValue(tertiaryValue, tertiaryOperator))

  return (
    <Stack>
      <IntroTitle variant='caption' color='grey600' fontWeight={600}>
        NEW RULE
      </IntroTitle>
      <Text variant='action3' color='grey800' marginBottom='0.35rem'>
        When employees match…
      </Text>
      <Text variant='body2' color='grey600' marginBottom='0.15rem'>
        Define up to three checks on worksite or profile data. We evaluate these
        after HRIS sync, onboarding, and worksite updates.
      </Text>

      <WhenSurface>
        <SectionEyebrow variant='body2' color='grey600' fontWeight={600}>
          Match conditions
        </SectionEyebrow>
        <RuleLogicEditor
          field={field}
          operator={operator}
          value={value}
          onFieldChange={setField}
          onOperatorChange={setOperator}
          onValueChange={setValue}
          addSecondCondition={addSecondCondition}
          onAddSecondConditionChange={handleAddSecondConditionChange}
          conditionJoin={conditionJoin}
          onConditionJoinChange={setConditionJoin}
          secondaryField={secondaryField}
          secondaryOperator={secondaryOperator}
          secondaryValue={secondaryValue}
          onSecondaryFieldChange={setSecondaryField}
          onSecondaryOperatorChange={setSecondaryOperator}
          onSecondaryValueChange={setSecondaryValue}
          addThirdCondition={addThirdCondition}
          onAddThirdConditionChange={handleAddThirdConditionChange}
          tertiaryConditionJoin={tertiaryConditionJoin}
          onTertiaryConditionJoinChange={setTertiaryConditionJoin}
          tertiaryField={tertiaryField}
          tertiaryOperator={tertiaryOperator}
          tertiaryValue={tertiaryValue}
          onTertiaryFieldChange={setTertiaryField}
          onTertiaryOperatorChange={setTertiaryOperator}
          onTertiaryValueChange={setTertiaryValue}
        />
      </WhenSurface>

      <ThenSurface>
        <SectionEyebrow variant='body2' color='grey600' fontWeight={600}>
          Then enroll into
        </SectionEyebrow>
        <EnrollmentTargetBlock
          groupItems={pickerItems}
          activeGroupItems={activeGroupItems}
          onActiveGroupItemsChange={setActiveGroupItems}
          tagItems={tagPickerItems}
          activeTagItems={activeTagItems}
          onActiveTagItemsChange={setActiveTagItems}
          removeNonMatchingMembers={removeNonMatchingMembers}
          onRemoveNonMatchingChange={setRemoveNonMatchingMembers}
          isActive={isActive}
          onActiveChange={setIsActive}
          showGroupControls={groupSegments.length > 0 || tagPickerItems.length > 0}
        />
      </ThenSurface>

      <Actions flexDirection='row'>
        <PrimaryButton
          onClick={handleAdd}
          disabled={
            !canAdd ||
            (groupSegments.length === 0 && tagPickerItems.length === 0)
          }
        >
          Add rule
        </PrimaryButton>
      </Actions>

      {groupSegments.length === 0 && tagPickerItems.length === 0 && (
        <Text variant='body2' color='grey600' marginTop='0.75rem'>
          Create a custom group on the Groups tab or a tag on the Tags tab before
          you can attach a rule.
        </Text>
      )}
    </Stack>
  )
}

export default React.memo(AddAutoEnrollRuleForm)
